/**
 * SectionTechPanel — Reusable technical detail wrapper for any rack unit section.
 * Applies the shared TechDetail design aesthetic consistently across the site.
 */

import React, { useState } from 'react';
import {
  tdPanel, tdHeaderRow, tdGlyph, tdName, tdCategoryBadge, tdSubtitle,
  tdToggleBtn, tdDescription, tdSummaryRow,
  MonoLabel, DetailSection, CATEGORY_COLORS,
} from '../styles/TechDetailStyles';

export interface TechPanelField {
  label: string;
  value: string | number;
}

export interface SectionTechPanelProps {
  /** Display glyph/icon */
  glyph?: string;
  /** Section title */
  title: string;
  /** Category for color coding */
  category?: string;
  /** Short subtitle / archetype */
  subtitle?: string;
  /** Description text */
  description?: string;
  /** Quick summary fields (label-value pairs) */
  fields?: TechPanelField[];
  /** Detailed content sections */
  children?: React.ReactNode;
  /** Start expanded? */
  defaultExpanded?: boolean;
  /** Collapsed height in px */
  collapsedHeight?: number;
  /** Expanded height in px */
  expandedHeight?: number;
  /** Accent override */
  accentColor?: string;
}

const SectionTechPanel: React.FC<SectionTechPanelProps> = ({
  glyph = '●',
  title,
  category = 'Default',
  subtitle,
  description,
  fields,
  children,
  defaultExpanded = false,
  collapsedHeight = 60,
  expandedHeight = 400,
  accentColor,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const catColor = accentColor ?? CATEGORY_COLORS[category] ?? '#71717a';

  return (
    <div style={{
      ...tdPanel,
      border: `1px solid ${catColor}33`,
      maxHeight: expanded ? expandedHeight : collapsedHeight,
      marginTop: 8,
    }}>
      {/* Header strip */}
      <div style={tdHeaderRow} onClick={() => setExpanded(e => !e)} title="Click to expand">
        <span style={tdGlyph}>{glyph}</span>
        <span style={tdName}>{title}</span>
        <span style={tdCategoryBadge(catColor)}>{category}</span>
        {subtitle && <span style={tdSubtitle}>{subtitle}</span>}
        <button style={tdToggleBtn} title={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? '▴' : '▾'}
        </button>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <>
          {description && <div style={tdDescription}>{description}</div>}

          {fields && fields.length > 0 && (
            <div style={tdSummaryRow}>
              {fields.map((f, i) => (
                <MonoLabel key={i} label={f.label} value={f.value} />
              ))}
            </div>
          )}

          {children}
        </>
      )}
    </div>
  );
};

export default SectionTechPanel;
